import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  DocumentCsvIcon,
  DocumentDocIcon,
  DocumentExcelIcon,
  DocumentJSONIcon,
  DocumentTextIcon,
  DocumentMiscIcon,
} from '../assets/documentIcons';
import LoadingSpinner from './LoadingSpinner';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDocument, deleteDocument } from '../services/apiService';
import { setSelectedDocument } from '../reducers/documentsReducer';
import { showToast } from '../reducers/uiReducer';
import { setSelectedPromptItem, removePromptItem } from '../reducers/uiReducer';
import { setLoading } from '../reducers/loadingReducer';
import useMediaQuery from '@mui/material/useMediaQuery';

const getFileIcon = (fileType) => {
  switch (fileType) {
    case 'text/csv':
      return <DocumentCsvIcon width={200} height={200} />;
    case 'application/msword':
    case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      return <DocumentDocIcon width={200} height={200} />;
    case 'application/vnd.ms-excel':
    case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
      return <DocumentExcelIcon width={200} height={200} />;
    case 'application/json':
      return <DocumentJSONIcon width={200} height={200} />;
    case 'text/plain':
      return <DocumentTextIcon width={200} height={200} />;
    default:
      return <DocumentMiscIcon width={200} height={200} />;
  }
};

const DocumentDetail = ({ selectedDocument }) => {
  const dispatch = useDispatch();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const promptItems = useSelector((state) => state.ui.promptItems);
  const isMobile = useMediaQuery('(max-width: 800px)');
  const isLoading = useSelector((state) => state.loading);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const fetchUserDocument = async (documentId) => {
    dispatch(setLoading(true));
    try {
      const response = await fetchDocument(documentId);
      dispatch(setSelectedDocument(response));
    } catch (error) {
      console.error('Error fetching document:', error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleDelete = async () => {
    dispatch(setLoading(true));
    try {
      await deleteDocument(selectedDocument.id, dispatch);
      dispatch(setSelectedDocument(null));
      dispatch(removePromptItem(selectedDocument.id));
      dispatch(showToast({ message: 'Document deleted successfully!', severity: 'success' }));
    } catch (error) {
      console.error('Failed to delete document:', error);
      dispatch(showToast({
        message: `Failed to delete document! - ${error.response.data.error}`,
        severity: 'error',
      }));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const checkAndUpdatePromptItem = (doc, items, dispatch) => {
    const match = items.find((item) => item.document?.id === doc.id);
    if (match) dispatch(setSelectedPromptItem(match));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: '2-digit', day: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };

  useEffect(() => {
    checkAndUpdatePromptItem(selectedDocument, promptItems, dispatch);
    fetchUserDocument(selectedDocument.id);
  }, []);

  return (
    <Box
      sx={{
        height: 310,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        backgroundColor: isDark ? theme.palette.background.paper : '#fff',
        p: 2,
        animation: 'fadeIn 0.4s ease-in-out',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {isLoading ? (
        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="100%">
          <LoadingSpinner size={30} />
          <Typography variant="body1" color="textSecondary">Loading...</Typography>
        </Box>
      ) : selectedDocument ? (
        <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" height="100%" gap={2}>
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="center" alignItems="center" gap={2}>
            <Box>{getFileIcon(selectedDocument.fileType)}</Box>
            <Box>
              <Typography variant="h6">{selectedDocument.fileName}</Typography>
              <Box display="flex" gap={1}><Typography fontWeight={500}>File Size:</Typography><Typography>{selectedDocument.fileSize}</Typography></Box>
              <Box display="flex" gap={1}><Typography fontWeight={500}>Uploaded:</Typography><Typography>{formatDate(selectedDocument.uploadDate)}</Typography></Box>
              <Box display="flex" gap={1} alignItems="center">
                <Typography fontWeight={500}>Indexed:</Typography>
                {selectedDocument.isIndexed ? <CheckCircleIcon color="success" /> : <CircularProgress size={20} />}
              </Box>
            </Box>
          </Box>
          <Box>
            {showConfirmDelete ? (
              <>
                <Button variant="contained" color="error" onClick={handleDelete} sx={{ mr: 2 }}>Confirm Delete</Button>
                <Button onClick={() => setShowConfirmDelete(false)}>Cancel</Button>
              </>
            ) : (
              <Button variant="outlined" color="error" onClick={() => setShowConfirmDelete(true)}>
                Delete
              </Button>
            )}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default DocumentDetail;
